/**
 * SMS Preview Helper
 * Utilities to ensure SMS apps can properly preview shared links
 */

import { extractVideoId } from '../services/youtubeService';

/**
 * Generate a share URL for a short that includes all necessary parameters
 * @param {Object} shortData - The short video data
 * @returns {string} - The shareable URL
 */
export const generateShareUrl = (shortData) => {
  if (!shortData || !shortData.shortUrl) {
    return window.location.origin + '/shorts';
  }
  
  // Extract video ID from YouTube URL to avoid URL encoding issues
  const videoId = extractVideoId(shortData.shortUrl);
  if (!videoId) {
    console.warn('Could not extract video ID from URL:', shortData.shortUrl);
    return window.location.origin + '/shorts';
  }
  
  const baseUrl = window.location.origin + '/shorts';
  const params = new URLSearchParams({
    v: videoId
  });
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Check if meta tags are properly set for SMS preview
 * @returns {Object} - Status of meta tags
 */
export const checkMetaTagsStatus = () => {
  const requiredTags = {
    'og:title': document.querySelector('meta[property="og:title"]')?.content,
    'og:description': document.querySelector('meta[property="og:description"]')?.content,
    'og:image': document.querySelector('meta[property="og:image"]')?.content,
    'og:url': document.querySelector('meta[property="og:url"]')?.content,
    'twitter:card': document.querySelector('meta[name="twitter:card"]')?.content,
    'twitter:image': document.querySelector('meta[name="twitter:image"]')?.content,
  };
  
  const missingTags = Object.entries(requiredTags)
    .filter(([key, value]) => !value)
    .map(([key]) => key);
  
  return {
    allTagsPresent: missingTags.length === 0,
    missingTags,
    tags: requiredTags
  };
};

/**
 * Test if a YouTube thumbnail URL is accessible
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} - The working thumbnail URL
 */
export const getWorkingThumbnail = async (videoId) => {
  if (!videoId) return null;
  
  const thumbnailOptions = [
    `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
  ];
  
  for (const url of thumbnailOptions) {
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      return url;
    } catch (e) {
      // Try next option
      continue;
    }
  }
  
  // Return default thumbnail as last resort
  return thumbnailOptions[thumbnailOptions.length - 1];
};

/**
 * Log SMS preview debug information
 * @param {Object} shortData - The short video data
 */
export const logSmsPreviewDebug = (shortData) => {
  if (process.env.NODE_ENV !== 'development') return;
  
  console.group('SMS Preview Debug Info');
  console.log('Short Data:', shortData);
  console.log('Share URL:', generateShareUrl(shortData));
  console.log('Meta Tags Status:', checkMetaTagsStatus());
  console.log('Current URL:', window.location.href);
  console.log('URL Parameters:', Object.fromEntries(new URLSearchParams(window.location.search)));
  console.groupEnd();
};

/**
 * Create a test SMS share link
 * @param {Object} shortData - The short video data
 * @returns {Object} - Object with share URLs for different platforms
 */
export const createTestShareLinks = (shortData) => {
  const shareUrl = generateShareUrl(shortData);
  const encodedUrl = encodeURIComponent(shareUrl);
  const text = encodeURIComponent(`Check out this video: ${shortData.title || 'Short Video'}`);
  
  return {
    sms: `sms:?&body=${text}%20${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${text}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${text}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}`,
  };
};